/* AddInstructionsComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./AddInstructionsRenderer");
const FileUploadUtility = require("./utils/FileUploadUtility");

const VALID_EXTENSIONS = [".pdf", ".docx"];

// Content type values from OdmInstructions.getContentTypeLabels()
const CONTENT_TYPE_BASE64_BINARY = "BASE64_BINARY";
const CONTENT_TYPE_TEXT = "TEXT";
// Not used in current implementation
// const CONTENT_TYPE_BASE64_TEXT = "BASE64_TEXT";

/**
 * Component for adding instructions to OS platforms
 * Supports three modes:
 * 1. Create from text - Manual text entry
 * 2. Create from file - Upload PDF/DOCX files
 * 3. Select existing - Link pre-existing instructions
 *
 * Also supports Edit Mode for editing existing instruction content.
 */
class AddInstructionsComponent extends EventEmitter {
    /**
     * Creates an instance of AddInstructionsComponent
     * @param {Object} logger - Logger instance
     * @param {Array<string>} selectedPlatforms - Array of platform IDs
     * @param {Array<Object>} odmOsInstructions - Available instructions for organization
     * @param {Function} OdmOsInstruction - OdmOsInstruction entity constructor
     * @param {Function} OdmOsPlatform - OdmOsPlatform entity constructor
     * @param {string} activeOrgId - Active organization ID
     * @param {Object} userSession - User session object
     * @param {boolean} isEditMode - Whether in edit mode
     * @param {Object} instructionToEdit - Instruction entity being edited (if edit mode)
     */
    constructor(
        logger,
        selectedPlatforms,
        odmOsInstructions,
        OdmOsInstruction,
        OdmOsPlatform,
        activeOrgId,
        userSession,
        isEditMode = false,
        instructionToEdit = null
    ) {
        super();
        this._logger = logger;
        this._selectedPlatforms = selectedPlatforms;
        this._odmOsInstructions = odmOsInstructions;
        this._activeOrgId = activeOrgId;
        this._OdmOsInstruction = OdmOsInstruction;
        this._OdmOsPlatform = OdmOsPlatform;
        this._userSession = userSession;
        this._isEditMode = isEditMode;
        this._instructionToEdit = instructionToEdit;
        this._instructionOptions = this._generateInstructionOptions();
        this._fileUploader = null;
        this._currentInstructionId = null;
        this._currentInstruction = null;
        this._summaryValues = {
            totalInstructions: 0,
            totalPlatforms: 0,
            total: 0,
            successCount: 0,
            failureCount: 0,
            skippedCount: 0,
            errors: [],
        };
        this._renderer = new Renderer(this, selectedPlatforms, isEditMode, instructionToEdit);
        this._renderer.render();
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    get selectedPlatforms() {
        return this._selectedPlatforms;
    }

    get isEditMode() {
        return this._isEditMode;
    }

    get instructionToEdit() {
        return this._instructionToEdit;
    }

    shutdown() {
        if (this._fileUploader) {
            this._fileUploader.destroy();
            this._fileUploader = null;
        }

        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._selectedPlatforms = undefined;
                this._odmOsInstructions = undefined;
                this._instructionOptions = undefined;
                this._renderer = undefined;
                this._userSession = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "AddInstructionsComponent.shutdown() error");
            }
        );
    }

    closeAction() {
        this._context.reject({userRejected: true});
    }

    getInstructionOptions() {
        return this._instructionOptions;
    }

    /**
     * Creates a new instruction from text and links it to selected platforms
     * @param {Object} instructionData - Instruction data from form
     * @param {string} instructionData.name - Instruction filename
     * @param {string} instructionData.contents - Instruction text content
     */
    createInstructionFromText(instructionData) {
        // In edit mode, update existing instruction instead of creating new one
        if (this._isEditMode && this._instructionToEdit) {
            return this._updateInstructionText(instructionData);
        }

        const newOdmOsInstruction = new this._OdmOsInstruction(this._activeOrgId);
        newOdmOsInstruction.filename = instructionData.name;
        newOdmOsInstruction.contents = instructionData.contents;
        newOdmOsInstruction.contentType = CONTENT_TYPE_TEXT;

        newOdmOsInstruction
            .create()
            .then(async (createdInstruction) => {
                // Link the new instruction to all selected platforms
                try {
                    await this._linkInstructionToPlatforms([createdInstruction.id]);
                    this._context.resolve({success: true, mode: "create", instructionData});
                    return Promise.resolve();
                } catch (err) {
                    return Promise.reject(err);
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "AddInstructionsComponent.createInstructionFromText() error"
                );
                const errorMsg = err.errorMsg || err.message || "Failed to create instructions";
                this.emit("ShowError", errorMsg);
            });
    }

    _updateInstructionText(instructionData) {
        this._instructionToEdit.setFilename(instructionData.name);
        this._instructionToEdit.setContents(instructionData.contents);

        this._instructionToEdit
            .update()
            .then(() => {
                this._context.resolve({success: true, mode: "edit", instructionData});
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "AddInstructionsComponent._updateInstructionText() error"
                );
                const errorMsg = err.errorMsg || err.message || "Failed to update instructions";
                this.emit("ShowError", errorMsg);
            });
    }

    /**
     * Uploads an instruction file (PDF/DOCX) and links it to selected platforms
     * @param {File} file - File object to upload
     */
    uploadInstructionFile(file) {
        const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        if (!VALID_EXTENSIONS.includes(fileExtension)) {
            const errorMsg = `Invalid file type. Only PDF and DOCX files are allowed.`;
            this._logger.warn({filename: file.name, extension: fileExtension}, errorMsg);
            this.emit("ShowError", errorMsg);
            return;
        }

        // In edit mode, update filename on existing instruction; otherwise create new one
        const instructionPromise =
            this._isEditMode && this._instructionToEdit
                ? (() => {
                      const currentFilename = this._instructionToEdit.getFilename();
                      if (currentFilename !== file.name) {
                          this._instructionToEdit.setFilename(file.name);
                          this._instructionToEdit.setContents(null);
                          this._instructionToEdit.setContentType(CONTENT_TYPE_BASE64_BINARY);
                          return this._instructionToEdit
                              .update()
                              .then(() => this._instructionToEdit);
                      }
                      return Promise.resolve(this._instructionToEdit);
                  })()
                : this._createNewInstructionEntity(file.name);

        instructionPromise
            .then((instruction) => {
                this._currentInstruction = instruction;
                this._currentInstructionId = instruction.id;
                const username = this._userSession?.user?.userName || "unknown";
                const metadata = {
                    username: username,
                    filename: file.name,
                    filetype: "binary",
                    destination: "odm",
                    organizationId: this._activeOrgId,
                    instructionId: instruction.id,
                };
                this._fileUploader = new FileUploadUtility({
                    file: file,
                    metadata: metadata,
                    endpoint: "/gfs/uploads/",
                    logger: this._logger,
                });

                this._fileUploader.on("UploadProgress", (bytesUploaded, bytesTotal, percentage) => {
                    this.emit("UploadProgress", bytesUploaded, bytesTotal, percentage);
                });

                this._fileUploader.on("UploadSuccess", (result) => {
                    this._linkInstructionToPlatforms([this._currentInstructionId])
                        .then(() => {
                            this._context.resolve({
                                success: true,
                                mode: "upload",
                                filename: file.name,
                                instructionId: this._currentInstructionId,
                            });
                        })
                        .catch((err) => {
                            this._logger.warn(
                                {error: err},
                                "Failed to link instructions to platforms after upload"
                            );
                            const errorMsg =
                                err.errorMsg ||
                                err.message ||
                                "Failed to link instructions to platforms";
                            this.emit("ShowError", errorMsg);
                        });
                });

                this._fileUploader.on("UploadError", (error) => {
                    this._logger.warn({error}, "File upload failed");
                    const errorMsg = error.message || "Failed to upload instruction file";
                    this.emit("ShowError", errorMsg);
                });

                this._fileUploader.on("UploadAborted", (result) => {
                    this._logger.info("Upload aborted");
                    if (this._currentInstruction) {
                        this._currentInstruction
                            .delete()
                            .then(() => {
                                this._currentInstruction = null;
                                this._currentInstructionId = null;
                            })
                            .catch((err) => {
                                this._logger.warn(
                                    {error: err},
                                    "Failed to delete instructions after abort"
                                );
                            });
                    }
                    this.emit("ShowError", "Upload was cancelled");
                });
                return this._fileUploader.start();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "Failed to create instructions entity");
                const errorMsg = err.errorMsg || err.message || "Failed to create instructions";
                this.emit("ShowError", errorMsg);
            });
    }

    abortUpload() {
        if (this._fileUploader && this._fileUploader.isUploading()) {
            this._logger.info("Aborting file upload");
            this._fileUploader.abort();
        }
    }

    resetUploadState() {
        if (this._fileUploader) {
            this._fileUploader.destroy();
            this._fileUploader = null;
        }
        this._currentInstructionId = null;
        this._currentInstruction = null;
    }

    /**
     * Links existing instructions to selected platforms
     * @param {Array<string>} instructionIds - Array of instruction IDs to link
     */
    async linkExistingInstructions(instructionIds) {
        this._summaryValues.totalInstructions = instructionIds.length;
        this._summaryValues.totalPlatforms = Array.isArray(this._selectedPlatforms)
            ? this._selectedPlatforms.length
            : 1;

        // Link all selected instructions to all selected platforms
        await this._linkInstructionToPlatforms(instructionIds);

        const failedLinks = this._summaryValues.failureCount;
        const skippedLinks = this._summaryValues.skippedCount;

        if (failedLinks > 0 || skippedLinks > 0) {
            this._context.resolve({
                success: false,
                mode: "link",
                instructionIds: instructionIds,
                totalInstructions: this._summaryValues.totalInstructions,
                totalPlatforms: this._summaryValues.totalPlatforms,
                total: this._summaryValues.total,
                successCount: this._summaryValues.successCount,
                failureCount: this._summaryValues.failureCount,
                skippedCount: this._summaryValues.skippedCount,
                errors: this._summaryValues.errors,
            });
        } else {
            this._context.resolve({success: true, mode: "link", instructionIds});
        }
    }

    set "$teve.ui.context"(context) {
        this._context = context;
    }

    /**
     * Links instructions to a single platform sequentially
     * @param {Object} platform - Platform entity
     * @param {Array<string>} instructionIds - Array of instruction IDs to link
     * @param {Array<string>} existingInstructionIds - Array of already-linked instruction IDs
     * @returns {Promise<Array>} Array of result objects
     * @private
     */
    _linkInstructionsToSinglePlatform(platform, instructionIds, existingInstructionIds) {
        // Process instructions sequentially - wait for each updateRelationships to complete
        return instructionIds.reduce((promiseChain, instructionId) => {
            return promiseChain.then((results) => {
                // Skip if already linked
                if (existingInstructionIds.indexOf(instructionId) >= 0) {
                    results.push({
                        status: "fulfilled",
                        value: {success: 0, skipped: 1, failed: 0, errors: []},
                    });
                    return results;
                }

                platform.addToInstructionsRelationship(instructionId);
                return platform
                    .updateRelationships()
                    .then(() => {
                        results.push({
                            status: "fulfilled",
                            value: {success: 1, skipped: 0, failed: 0, errors: []},
                        });
                        return results;
                    })
                    .catch((err) => {
                        platform.clearPendingRelationships();
                        this._logger.warn(
                            {error: err, instructionId},
                            "Failed to update platform relationships"
                        );
                        results.push({
                            status: "fulfilled",
                            value: {
                                success: 0,
                                skipped: 0,
                                failed: 1,
                                errors: [
                                    err.errorMsg ||
                                        err.message ||
                                        "Failed to link instructions to platform",
                                ],
                            },
                        });
                        return results;
                    });
            });
        }, Promise.resolve([]));
    }

    /**
     * Links instructions to platform entities - generic method for both edit and bulk add modes
     * @param {Array<string>} instructionIds - Array of instruction IDs to link
     * @param {Array<Object>} platforms - Array of platform entities
     * @returns {Promise} Promise that resolves when all instructions are linked
     * @private
     */
    async _linkInstructionsToPlatforms(instructionIds, platforms) {
        const platformEntities = Array.isArray(platforms) ? platforms : [];

        if (platformEntities.length === 0) {
            this._logger.warn(
                "AddInstructionsComponent._linkInstructionsToPlatforms() - No platforms to link"
            );
            return Promise.resolve();
        }

        // Process each platform entity (link all instructions sequentially per platform)
        const platformPromises = platformEntities.map((platform) => {
            return this._getExistingInstructionIds(platform).then((existingInstructionIds) => {
                return this._linkInstructionsToSinglePlatform(
                    platform,
                    instructionIds,
                    existingInstructionIds
                );
            });
        });

        // Wait for all platforms to complete
        const platformResults = await Promise.allSettled(platformPromises);

        // Aggregate all results from all platforms
        platformResults.forEach((platformResult) => {
            if (platformResult.status === "fulfilled") {
                // Each platform returns an array of instruction results
                this._aggregateResults(platformResult.value);
            } else {
                // Platform promise rejected unexpectedly
                this._logger.warn(
                    {error: platformResult.reason},
                    "Platform processing failed unexpectedly"
                );
                instructionIds.forEach(() => {
                    this._summaryValues.total += 1;
                    this._summaryValues.failureCount += 1;
                });
                this._summaryValues.errors.push(
                    platformResult.reason?.message || "Platform processing failed"
                );
            }
        });
    }

    /**
     * Aggregates results from instruction linking operations
     * @param {Array} results - Array of result objects
     * @private
     */
    _aggregateResults(results) {
        results.forEach((result) => {
            if (result.status === "fulfilled" && result.value) {
                this._summaryValues.total += 1;
                this._summaryValues.successCount += result.value.success || 0;
                this._summaryValues.failureCount += result.value.failed || 0;
                this._summaryValues.skippedCount += result.value.skipped || 0;
                if (result.value.errors && result.value.errors.length > 0) {
                    this._summaryValues.errors.push(...result.value.errors);
                }
            } else {
                // Promise rejected unexpectedly
                this._summaryValues.total += 1;
                this._summaryValues.failureCount += 1;
                this._summaryValues.errors.push(result.reason?.message || "Unexpected error");
            }
        });
    }

    /**
     * Links instructions to all selected platforms
     * Loads all platform entities upfront and passes them to _linkInstructionsToPlatforms
     * @param {Array<string>} instructionIds - Array of instruction IDs to link
     * @returns {Promise} Promise that resolves when all platforms are updated
     * @private
     */
    async _linkInstructionToPlatforms(instructionIds) {
        let platformEntities = [];
        // Load all platform entities in one call
        const platformIds = Array.isArray(this._selectedPlatforms)
            ? this._selectedPlatforms
            : [this._selectedPlatforms];

        if (platformIds.length === 0) {
            this._logger.warn(
                "AddInstructionsComponent._linkInstructionToPlatforms() - No platforms selected"
            );
            return;
        }

        // Load all platforms in a single batch query
        platformEntities = await this._OdmOsPlatform.loadByQuery(
            this._activeOrgId,
            this._OdmOsPlatform.getEntityType(),
            {id: {$in: platformIds}}
        );

        if (platformEntities.length === 0) {
            this._logger.warn(
                "AddInstructionsComponent._linkInstructionToPlatforms() - All platforms failed to load"
            );
            // Mark all as failed
            const totalAttempts = platformIds.length * instructionIds.length;
            this._summaryValues.total = totalAttempts;
            this._summaryValues.failureCount = totalAttempts;
            this._summaryValues.errors.push("All platforms failed to load");
            return;
        }

        // Pass loaded entities to generic linking method
        return this._linkInstructionsToPlatforms(instructionIds, platformEntities);
    }

    _createNewInstructionEntity(filename) {
        const newOdmOsInstruction = new this._OdmOsInstruction(this._activeOrgId);
        newOdmOsInstruction.filename = filename;
        newOdmOsInstruction.contentType = CONTENT_TYPE_BASE64_BINARY;
        return newOdmOsInstruction.create();
    }

    /**
     * Generates instruction options for the combobox
     * @returns {Array<Object>} Array of {label, value} objects
     * @private
     */
    _generateInstructionOptions() {
        return this._odmOsInstructions.map((instruction) => ({
            label: instruction.filename,
            value: instruction.id,
        }));
    }

    /**
     * Gets existing instructions linked to a platform
     * @param {Object} platform - Platform entity
     * @returns {Promise<Array>} Array of instruction entities
     * @private
     */
    _getExistingInstructions(platform) {
        return platform.loadRelatedInstructions({}, {id: true, filename: true});
    }

    /**
     * Gets IDs of existing instructions linked to a platform
     * @param {Object} platform - Platform entity
     * @param {Array} existingInstructions - Optional pre-loaded instructions
     * @param {boolean} instructionsLoaded - Whether instructions are already loaded
     * @returns {Promise<Array<string>>} Array of instruction IDs
     * @private
     */
    _getExistingInstructionIds(platform, existingInstructions, instructionsLoaded = false) {
        if (instructionsLoaded) {
            return Promise.resolve(existingInstructions.map((instruction) => instruction.id));
        }
        return this._getExistingInstructions(platform).then((instructions) =>
            instructions.map((instruction) => instruction.id)
        );
    }
}

module.exports = AddInstructionsComponent;
